import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Coffee, ExternalLink, Check, XCircle, MessageCircle, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

const STATUS = {
  proposed:  { c: "bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay-2))]", label: "Proposed" },
  accepted:  { c: "bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))]", label: "Accepted" },
  declined:  { c: "bg-destructive/10 text-destructive", label: "Declined" },
  completed: { c: "bg-muted text-muted-foreground", label: "Completed" },
  cancelled: { c: "bg-muted text-muted-foreground", label: "Cancelled" },
};

export default function CoffeeChatsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/coffee-chats"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action, payload) => {
    try {
      const url = `/coffee-chats/${id}/${action}`;
      const { data } = await api.post(url, payload || {});
      setRows((rs) => rs.map((r) => r.id === id ? data : r));
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const handleAccept = async (id) => {
    setAcceptingId(id);
    const data = await act(id, "accept", {});
    setAcceptingId(null);
    if (data?.thread_id) {
      toast.success("Coffee chat accepted — opening chat…");
      navigate(`/messages/${data.thread_id}`);
    }
  };

  return (
    <SiteShell>
      <section className="container-page py-14" data-testid={TID.coffeeChats.root}>
        <span className="chip"><Coffee size={14} /> Community</span>
        <h1 className="mt-3 font-serif text-4xl tracking-tight">Coffee Chats</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl leading-relaxed">
          A Coffee Chat is a private message conversation that lets authors and beta readers discuss a project before committing to work together. Use it to ask questions, clarify expectations, discuss availability, and see whether the project feels like a good fit. It is not a scheduled video or phone call.
        </p>

        <div className="mt-10 space-y-4">
          {loading && Array.from({ length: 2 }).map((_, i) => <div key={i} className="card-soft h-40 animate-pulse" />)}
          {!loading && rows.length === 0 && (
            <div className="card-soft text-center py-16 sm:py-20">
              <div className="mx-auto w-16 h-16 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center">
                <Coffee size={26} className="text-[hsl(var(--sb-forest-2))]" />
              </div>
              <h3 className="mt-5 font-serif text-2xl">No coffee chats yet — start with a hello</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                A Coffee Chat is a private message conversation that lets authors and beta readers discuss a project before committing to work together. Send a short intro and, if the other person accepts, a private chat will open.
              </p>
              <Link to="/jobs" className="btn-primary inline-flex mt-6">Browse projects to spark one</Link>
            </div>
          )}
          {rows.map((r) => (
            <ChatRow key={r.id} chat={r} currentUser={user}
              accepting={acceptingId === r.id}
              onAccept={() => handleAccept(r.id)}
              onDecline={() => act(r.id, "decline")}
              onComplete={() => act(r.id, "complete")} />
          ))}
        </div>
      </section>
    </SiteShell>
  );
}

function ChatRow({ chat, currentUser, accepting, onAccept, onDecline, onComplete }) {
  const s = STATUS[chat.status] || STATUS.proposed;
  const isRequester = chat.requester_id === currentUser?.id;
  const canRespond = chat.status === "proposed" && !isRequester;
  // The counterparty is whichever of author / reader is NOT the current
  // user. Backend now includes both display names on the chat payload.
  const otherIsAuthor = chat.author_id !== currentUser?.id;
  const otherName =
    (otherIsAuthor ? chat.author_display_name : chat.reader_display_name) || "";
  // Early Access read-only state — server exposes counterparty's EA flag
  // via ``counterparty_early_access_active``. Viewer is EA-revoked when
  // their own ``is_private_test`` is false. **Admin viewers bypass the
  // gate entirely** — they never lose access to Coffee Chat controls
  // regardless of their own EA record or the counterparty's revocation
  // status. Backend accept/decline endpoints already bypass admin, so
  // hiding the notice here just matches server-side behaviour.
  const viewerIsAdmin = (currentUser?.roles || []).includes("admin");
  const viewerRevoked = !viewerIsAdmin && currentUser && currentUser.is_private_test === false;
  const otherRevoked = !viewerIsAdmin && chat.counterparty_early_access_active === false;
  const eaBlocked = viewerRevoked || otherRevoked;
  const eaBlockNotice = viewerRevoked
    ? "Your Early Access is no longer active. You can't accept or decline Coffee Chats right now."
    : `${otherName || "This member"}'s Early Access is no longer active. This Coffee Chat can't be continued right now.`;
  return (
    <article className="card-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className={`text-[10px] uppercase tracking-[0.18em] rounded-full px-2.5 py-1 ${s.c}`}>{s.label}</span>
          <h3 className="mt-3 font-serif text-xl">
            {isRequester ? "You invited " : "Invited by "}
            {chat.author_id === currentUser?.id ? "your reader" : chat.reader_id === currentUser?.id ? "an author" : "a peer"}
          </h3>
        </div>
        {chat.job_id && (
          <Link to={`/jobs/${chat.job_id}`} className="text-xs text-muted-foreground link-underline">
            Linked project <ExternalLink size={12} className="inline" />
          </Link>
        )}
      </div>
      <p className="mt-3 text-sm text-foreground/85 leading-relaxed whitespace-pre-line">{chat.message}</p>
      {chat.status === "accepted" && chat.thread_id && (
        <div
          className="mt-4 rounded-xl border border-[hsl(var(--sb-forest))]/30 bg-[hsl(var(--sb-moss))]/40 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid={`coffee-chat-accepted-cta-${chat.id}`}
        >
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <Check size={16} className="mt-0.5 text-[hsl(var(--sb-forest-2))] shrink-0" />
            <div className="text-sm leading-relaxed text-[hsl(var(--sb-forest-2))]">
              <span className="font-semibold">Coffee Chat accepted!</span>{" "}
              You can now message
              {otherName ? <span className="font-semibold"> {otherName}</span> : " this person"}
              .
            </div>
          </div>
          <Link
            to={`/messages/${chat.thread_id}`}
            className="btn-primary shrink-0 whitespace-nowrap"
            data-testid={`coffee-chat-open-conversation-${chat.id}`}
          >
            <MessageCircle size={14} /> Open conversation
          </Link>
        </div>
      )}
      <div className="mt-5 flex gap-2 flex-wrap">
        {canRespond && !eaBlocked && (
          <>
            <button onClick={onAccept} disabled={accepting} className="btn-primary" data-testid={TID.coffeeChats.accept}>
              <Check size={14} /> {accepting ? "Accepting…" : "Accept & open chat"}
            </button>
            <button onClick={onDecline} disabled={accepting} className="btn-ghost border" data-testid={TID.coffeeChats.decline}><XCircle size={14} /> Decline</button>
          </>
        )}
        {canRespond && eaBlocked && (
          <div
            className="w-full rounded-xl border border-[hsl(var(--sb-clay))]/30 bg-[hsl(var(--sb-clay))]/10 px-3.5 py-2.5 text-xs md:text-sm text-foreground/85 flex items-start gap-2"
            data-testid={`coffee-chat-ea-blocked-${chat.id}`}
          >
            <ShieldOff size={14} className="mt-0.5 shrink-0 text-[hsl(var(--sb-clay))]" />
            <span>{eaBlockNotice}</span>
          </div>
        )}
        {chat.status === "accepted" && (
          <button onClick={onComplete} className="btn-ghost border" data-testid={TID.coffeeChats.complete}>Mark completed</button>
        )}
      </div>
    </article>
  );
}
